import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createTextVNode as _createTextVNode, createElementVNode as _createElementVNode, withCtx as _withCtx, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, withModifiers as _withModifiers, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "mb-4" }
const _hoisted_2 = { class: "my-3" }
const _hoisted_3 = { for: "user-role" }
const _hoisted_4 = { key: 0 }
const _hoisted_5 = { key: 1 }

import { computed, ref } from 'vue';
import { formatDateTimeStringToIso } from '@/utils/date-utils';
import {
  BButton,
  BForm,
  BFormGroup,
  BFormInput,
  BSpinner,
  BTooltip,
} from 'bootstrap-vue-next';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { PAGING_ITEMS_PER_PAGE } from '@/constants';
import ComputeTaskList from '@/components/compute/ComputeTaskList.vue';

// --- Component references ---

export default /*@__PURE__*/_defineComponent({
  __name: 'AdminComputeTasksView',
  setup(__props) {

const ComputeTaskListRef = ref<InstanceType<typeof ComputeTaskList>>();

// Filtering and paging state
const taskId = ref<number | null>(null);
const parentId = ref<number | null>(null);
const userId = ref<number | null>(null);
const userRole = ref<string | null>(null);
const projectId = ref<number | null>(null);
const bucketId = ref<number | null>(null);
const taskType = ref('');
const status = ref('');
const namePattern = ref('');

const dateCreatedAfter = ref<string | null>(null);
const dateCreatedBefore = ref<string | null>(null);
const timeCreatedAfter = ref<string | null>(null);
const timeCreatedBefore = ref<string | null>(null);
const dateTimeCreatedAfter = ref<string | null>(null);
const dateTimeCreatedBefore = ref<string | null>(null);

const dateUpdatedAfter = ref<string | null>(null);
const dateUpdatedBefore = ref<string | null>(null);
const timeUpdatedAfter = ref<string | null>(null);
const timeUpdatedBefore = ref<string | null>(null);
const dateTimeUpdatedAfter = ref<string | null>(null);
const dateTimeUpdatedBefore = ref<string | null>(null);

const dateCompletedAfter = ref<string | null>(null);
const dateCompletedBefore = ref<string | null>(null);
const timeCompletedAfter = ref<string | null>(null);
const timeCompletedBefore = ref<string | null>(null);
const dateTimeCompletedAfter = ref<string | null>(null);
const dateTimeCompletedBefore = ref<string | null>(null);

const dateAbortedAfter = ref<string | null>(null);
const dateAbortedBefore = ref<string | null>(null);
const timeAbortedAfter = ref<string | null>(null);
const timeAbortedBefore = ref<string | null>(null);
const dateTimeAbortedAfter = ref<string | null>(null);
const dateTimeAbortedBefore = ref<string | null>(null);

const pagingItemsPerPage = ref(PAGING_ITEMS_PER_PAGE);

const acrossUsers = computed(() => {
  return userId.value === null;
});

function clearFilter() {
  taskId.value = null;
  userId.value = null;
  projectId.value = null;
  bucketId.value = null;
  taskType.value = '';
  status.value = '';
  namePattern.value = '';

  dateCreatedBefore.value = null;
  timeCreatedBefore.value = null;
  dateTimeCreatedBefore.value = null;

  dateCreatedAfter.value = null;
  timeCreatedAfter.value = null;
  dateTimeCreatedAfter.value = null;

  dateUpdatedBefore.value = null;
  timeUpdatedBefore.value = null;
  dateTimeUpdatedBefore.value = null;

  dateUpdatedAfter.value = null;
  timeUpdatedAfter.value = null;
  dateTimeUpdatedAfter.value = null;

  dateCompletedBefore.value = null;
  timeCompletedBefore.value = null;
  dateTimeCompletedBefore.value = null;

  dateCompletedAfter.value = null;
  timeCompletedAfter.value = null;
  dateTimeCompletedAfter.value = null;

  dateAbortedBefore.value = null;
  timeAbortedBefore.value = null;
  dateTimeAbortedBefore.value = null;

  dateAbortedAfter.value = null;
  timeAbortedAfter.value = null;
  dateTimeAbortedAfter.value = null;

  requestSearch();
}

function requestSearch() {
  // check integer values are integer values
  if (!Number.isInteger(taskId.value)) {
    console.warn('Invalid task ID. Resetting to null.');
    taskId.value = null;
  }
  if (!Number.isInteger(parentId.value)) {
    console.warn('Invalid parent ID. Resetting to null.');
    parentId.value = null;
  }
  if (!Number.isInteger(userId.value)) {
    console.warn('Invalid user ID. Resetting to null.');
    userId.value = null;
  }
  if (!Number.isInteger(projectId.value)) {
    console.warn('Invalid project ID. Resetting to null.');
    projectId.value = null;
  }
  if (!Number.isInteger(bucketId.value)) {
    console.warn('Invalid bucket ID. Resetting to null.');
    bucketId.value = null;
  }

  // convert date and time to dateTime object (current timezone)
  dateTimeCreatedBefore.value = formatDateTimeStringToIso(
    dateCreatedBefore.value,
    timeCreatedBefore.value
  );
  dateTimeCreatedAfter.value = formatDateTimeStringToIso(
    dateCreatedAfter.value,
    timeCreatedAfter.value
  );
  dateTimeUpdatedBefore.value = formatDateTimeStringToIso(
    dateUpdatedBefore.value,
    timeUpdatedBefore.value
  );
  dateTimeUpdatedAfter.value = formatDateTimeStringToIso(
    dateUpdatedAfter.value,
    timeUpdatedAfter.value
  );
  dateTimeCompletedBefore.value = formatDateTimeStringToIso(
    dateCompletedBefore.value,
    timeCompletedBefore.value
  );
  dateTimeCompletedAfter.value = formatDateTimeStringToIso(
    dateCompletedAfter.value,
    timeCompletedAfter.value
  );
  dateTimeAbortedBefore.value = formatDateTimeStringToIso(
    dateAbortedBefore.value,
    timeAbortedBefore.value
  );
  dateTimeAbortedAfter.value = formatDateTimeStringToIso(
    dateAbortedAfter.value,
    timeAbortedAfter.value
  );

  // refetch the tasks with updated filters
  ComputeTaskListRef.value?.requestSearch();
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("h1", _hoisted_1, [
      _createVNode(_unref(FontAwesomeIcon), { icon: ['fas', 'list-check'] }),
      _cache[26] || (_cache[26] = _createTextVNode(" Compute tasks ", -1))
    ]),
    _cache[32] || (_cache[32] = _createElementVNode("h5", null, "Browse and manage compute tasks", -1)),
    _cache[33] || (_cache[33] = _createElementVNode("hr", null, null, -1)),
    _createElementVNode("div", null, [
      _createElementVNode("div", _hoisted_2, [
        _createVNode(_unref(BForm), {
          class: "d-flex flex-row align-items-end flex-wrap gap-2",
          onSubmit: _withModifiers(requestSearch, ["prevent"])
        }, {
          default: _withCtx(() => [
            _createVNode(_unref(BFormGroup), {
              label: "Task ID",
              "label-for": "task-id"
            }, {
              default: _withCtx(() => [
                _createVNode(_unref(BFormInput), {
                  id: "task-id",
                  type: "number",
                  modelValue: taskId.value,
                  "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((taskId).value = $event)),
                  modelModifiers: { number: true },
                  placeholder: "Task ID"
                }, null, 8, ["modelValue"])
              ]),
              _: 1
            }),
            _createVNode(_unref(BFormGroup), {
              label: "Parent Task ID",
              "label-for": "parent-id"
            }, {
              default: _withCtx(() => [
                _createVNode(_unref(BFormInput), {
                  id: "parent-id",
                  type: "number",
                  modelValue: parentId.value,
                  "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((parentId).value = $event)),
                  modelModifiers: { number: true },
                  placeholder: "Parent Task ID"
                }, null, 8, ["modelValue"])
              ]),
              _: 1
            }),
            _createVNode(_unref(BFormGroup), {
              label: "User ID",
              "label-for": "user-id"
            }, {
              default: _withCtx(() => [
                _createVNode(_unref(BFormInput), {
                  id: "user-id",
                  type: "number",
                  modelValue: userId.value,
                  "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((userId).value = $event)),
                  modelModifiers: { number: true },
                  placeholder: "User ID"
                }, null, 8, ["modelValue"])
              ]),
              _: 1
            }),
            _createVNode(_unref(BFormGroup), {
              label: "User ID",
              "label-for": "user-id"
            }, {
              default: _withCtx(() => [
                _createVNode(_unref(BFormInput), {
                  id: "user-id",
                  type: "number",
                  modelValue: userId.value,
                  "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((userId).value = $event)),
                  modelModifiers: { number: true },
                  placeholder: "User ID"
                }, null, 8, ["modelValue"])
              ]),
              _: 1
            }),
            _createVNode(_unref(BFormGroup), null, {
              default: _withCtx(() => [
                _createElementVNode("label", _hoisted_3, [
                  _cache[28] || (_cache[28] = _createTextVNode(" User role ", -1)),
                  _createVNode(_unref(FontAwesomeIcon), {
                    id: "user-role-tooltip-icon",
                    icon: "question-circle",
                    class: "ms-1 text-secondary"
                  }),
                  _createVNode(_unref(BTooltip), { target: "user-role-tooltip-icon" }, {
                    default: _withCtx(() => [...(_cache[27] || (_cache[27] = [
                      _createTextVNode(" Only applicable if user ID is specified. ", -1)
                    ]))]),
                    _: 1
                  })
                ]),
                _createVNode(_unref(BFormInput), {
                  id: "user-role",
                  type: "text",
                  modelValue: userRole.value,
                  "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((userRole).value = $event)),
                  placeholder: "e.g. owner, editor, viewer"
                }, null, 8, ["modelValue"])
              ]),
              _: 1
            }),
            _createVNode(_unref(BFormGroup), {
              label: "Project ID",
              "label-for": "project-id"
            }, {
              default: _withCtx(() => [
                _createVNode(_unref(BFormInput), {
                  id: "project-id",
                  type: "number",
                  modelValue: projectId.value,
                  "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((projectId).value = $event)),
                  modelModifiers: { number: true },
                  placeholder: "Project ID"
                }, null, 8, ["modelValue"])
              ]),
              _: 1
            }),
            _createVNode(_unref(BFormGroup), {
              label: "Bucket ID",
              "label-for": "bucket-id"
            }, {
              default: _withCtx(() => [
                _createVNode(_unref(BFormInput), {
                  id: "bucket-id",
                  type: "number",
                  modelValue: bucketId.value,
                  "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((bucketId).value = $event)),
                  modelModifiers: { number: true },
                  placeholder: "Bucket ID"
                }, null, 8, ["modelValue"])
              ]),
              _: 1
            }),
            _createVNode(_unref(BFormGroup), {
              label: "Task type",
              "label-for": "search-action"
            }, {
              default: _withCtx(() => [
                _createVNode(_unref(BFormInput), {
                  id: "task-type",
                  type: "text",
                  modelValue: taskType.value,
                  "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((taskType).value = $event)),
                  placeholder: "e.g. DATA_PIPELINE"
                }, null, 8, ["modelValue"])
              ]),
              _: 1
            }),
            _createVNode(_unref(BFormGroup), {
              label: "Status",
              "label-for": "task-status"
            }, {
              default: _withCtx(() => [
                _createVNode(_unref(BFormInput), {
                  id: "task-status",
                  type: "text",
                  modelValue: status.value,
                  "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((status).value = $event)),
                  placeholder: "e.g. RUNNING"
                }, null, 8, ["modelValue"])
              ]),
              _: 1
            }),
            _createVNode(_unref(BFormGroup), {
              label: "Task name",
              "label-for": "task-name"
            }, {
              default: _withCtx(() => [
                _createVNode(_unref(BFormInput), {
                  id: "search-text",
                  type: "text",
                  modelValue: namePattern.value,
                  "onUpdate:modelValue": _cache[9] || (_cache[9] = ($event: any) => ((namePattern).value = $event)),
                  placeholder: "e.g. CD% reconstruction"
                }, null, 8, ["modelValue"])
              ]),
              _: 1
            }),
            _createVNode(_unref(BFormGroup), {
              label: "Date created after",
              "label-for": "date-created-after"
            }, {
              default: _withCtx(() => [
                _createVNode(_unref(BFormInput), {
                  id: "date-created-after",
                  type: "date",
                  modelValue: dateCreatedAfter.value,
                  "onUpdate:modelValue": _cache[10] || (_cache[10] = ($event: any) => ((dateCreatedAfter).value = $event))
                }, null, 8, ["modelValue"])
              ]),
              _: 1
            }),
            _createVNode(_unref(BFormGroup), {
              label: "Time created after",
              "label-for": "time-created-after"
            }, {
              default: _withCtx(() => [
                _createVNode(_unref(BFormInput), {
                  id: "time-created-after",
                  type: "time",
                  modelValue: timeCreatedAfter.value,
                  "onUpdate:modelValue": _cache[11] || (_cache[11] = ($event: any) => ((timeCreatedAfter).value = $event))
                }, null, 8, ["modelValue"])
              ]),
              _: 1
            }),
            _createVNode(_unref(BFormGroup), {
              label: "Date created before",
              "label-for": "date-created-before"
            }, {
              default: _withCtx(() => [
                _createVNode(_unref(BFormInput), {
                  id: "date-created-before",
                  type: "date",
                  modelValue: dateCreatedBefore.value,
                  "onUpdate:modelValue": _cache[12] || (_cache[12] = ($event: any) => ((dateCreatedBefore).value = $event))
                }, null, 8, ["modelValue"])
              ]),
              _: 1
            }),
            _createVNode(_unref(BFormGroup), {
              label: "Time created before",
              "label-for": "time-created-before"
            }, {
              default: _withCtx(() => [
                _createVNode(_unref(BFormInput), {
                  id: "time-created-before",
                  type: "time",
                  modelValue: timeCreatedBefore.value,
                  "onUpdate:modelValue": _cache[13] || (_cache[13] = ($event: any) => ((timeCreatedBefore).value = $event))
                }, null, 8, ["modelValue"])
              ]),
              _: 1
            }),
            _createVNode(_unref(BFormGroup), {
              label: "Date updated after",
              "label-for": "date-updated-after"
            }, {
              default: _withCtx(() => [
                _createVNode(_unref(BFormInput), {
                  id: "date-updated-after",
                  type: "date",
                  modelValue: dateUpdatedAfter.value,
                  "onUpdate:modelValue": _cache[14] || (_cache[14] = ($event: any) => ((dateUpdatedAfter).value = $event))
                }, null, 8, ["modelValue"])
              ]),
              _: 1
            }),
            _createVNode(_unref(BFormGroup), {
              label: "Time updated after",
              "label-for": "time-updated-after"
            }, {
              default: _withCtx(() => [
                _createVNode(_unref(BFormInput), {
                  id: "time-updated-after",
                  type: "time",
                  modelValue: timeUpdatedAfter.value,
                  "onUpdate:modelValue": _cache[15] || (_cache[15] = ($event: any) => ((timeUpdatedAfter).value = $event))
                }, null, 8, ["modelValue"])
              ]),
              _: 1
            }),
            _createVNode(_unref(BFormGroup), {
              label: "Date updated before",
              "label-for": "date-updated-before"
            }, {
              default: _withCtx(() => [
                _createVNode(_unref(BFormInput), {
                  id: "date-updated-before",
                  type: "date",
                  modelValue: dateUpdatedBefore.value,
                  "onUpdate:modelValue": _cache[16] || (_cache[16] = ($event: any) => ((dateUpdatedBefore).value = $event))
                }, null, 8, ["modelValue"])
              ]),
              _: 1
            }),
            _createVNode(_unref(BFormGroup), {
              label: "Time updated before",
              "label-for": "time-updated-before"
            }, {
              default: _withCtx(() => [
                _createVNode(_unref(BFormInput), {
                  id: "time-updated-before",
                  type: "time",
                  modelValue: timeUpdatedBefore.value,
                  "onUpdate:modelValue": _cache[17] || (_cache[17] = ($event: any) => ((timeUpdatedBefore).value = $event))
                }, null, 8, ["modelValue"])
              ]),
              _: 1
            }),
            _createVNode(_unref(BFormGroup), {
              label: "Date completed after",
              "label-for": "date-completed-after"
            }, {
              default: _withCtx(() => [
                _createVNode(_unref(BFormInput), {
                  id: "date-completed-after",
                  type: "date",
                  modelValue: dateCompletedAfter.value,
                  "onUpdate:modelValue": _cache[18] || (_cache[18] = ($event: any) => ((dateCompletedAfter).value = $event))
                }, null, 8, ["modelValue"])
              ]),
              _: 1
            }),
            _createVNode(_unref(BFormGroup), {
              label: "Time completed after",
              "label-for": "time-completed-after"
            }, {
              default: _withCtx(() => [
                _createVNode(_unref(BFormInput), {
                  id: "time-completed-after",
                  type: "time",
                  modelValue: timeCompletedAfter.value,
                  "onUpdate:modelValue": _cache[19] || (_cache[19] = ($event: any) => ((timeCompletedAfter).value = $event))
                }, null, 8, ["modelValue"])
              ]),
              _: 1
            }),
            _createVNode(_unref(BFormGroup), {
              label: "Date completed before",
              "label-for": "date-completed-before"
            }, {
              default: _withCtx(() => [
                _createVNode(_unref(BFormInput), {
                  id: "date-completed-before",
                  type: "date",
                  modelValue: dateCompletedBefore.value,
                  "onUpdate:modelValue": _cache[20] || (_cache[20] = ($event: any) => ((dateCompletedBefore).value = $event))
                }, null, 8, ["modelValue"])
              ]),
              _: 1
            }),
            _createVNode(_unref(BFormGroup), {
              label: "Time completed before",
              "label-for": "time-completed-before"
            }, {
              default: _withCtx(() => [
                _createVNode(_unref(BFormInput), {
                  id: "time-completed-before",
                  type: "time",
                  modelValue: timeCompletedBefore.value,
                  "onUpdate:modelValue": _cache[21] || (_cache[21] = ($event: any) => ((timeCompletedBefore).value = $event))
                }, null, 8, ["modelValue"])
              ]),
              _: 1
            }),
            _createVNode(_unref(BFormGroup), {
              label: "Date aborted after",
              "label-for": "date-aborted-after"
            }, {
              default: _withCtx(() => [
                _createVNode(_unref(BFormInput), {
                  id: "date-aborted-after",
                  type: "date",
                  modelValue: dateAbortedAfter.value,
                  "onUpdate:modelValue": _cache[22] || (_cache[22] = ($event: any) => ((dateAbortedAfter).value = $event))
                }, null, 8, ["modelValue"])
              ]),
              _: 1
            }),
            _createVNode(_unref(BFormGroup), {
              label: "Time aborted after",
              "label-for": "time-aborted-after"
            }, {
              default: _withCtx(() => [
                _createVNode(_unref(BFormInput), {
                  id: "time-aborted-after",
                  type: "time",
                  modelValue: timeAbortedAfter.value,
                  "onUpdate:modelValue": _cache[23] || (_cache[23] = ($event: any) => ((timeAbortedAfter).value = $event))
                }, null, 8, ["modelValue"])
              ]),
              _: 1
            }),
            _createVNode(_unref(BFormGroup), {
              label: "Date aborted before",
              "label-for": "date-aborted-before"
            }, {
              default: _withCtx(() => [
                _createVNode(_unref(BFormInput), {
                  id: "date-aborted-before",
                  type: "date",
                  modelValue: dateAbortedBefore.value,
                  "onUpdate:modelValue": _cache[24] || (_cache[24] = ($event: any) => ((dateAbortedBefore).value = $event))
                }, null, 8, ["modelValue"])
              ]),
              _: 1
            }),
            _createVNode(_unref(BFormGroup), {
              label: "Time aborted before",
              "label-for": "time-aborted-before"
            }, {
              default: _withCtx(() => [
                _createVNode(_unref(BFormInput), {
                  id: "time-aborted-before",
                  type: "time",
                  modelValue: timeAbortedBefore.value,
                  "onUpdate:modelValue": _cache[25] || (_cache[25] = ($event: any) => ((timeAbortedBefore).value = $event))
                }, null, 8, ["modelValue"])
              ]),
              _: 1
            }),
            _createVNode(_unref(BButton), {
              variant: "outline-secondary",
              onClick: clearFilter
            }, {
              default: _withCtx(() => [
                _createVNode(_unref(FontAwesomeIcon), {
                  icon: "trash",
                  class: "me-1"
                }),
                _cache[29] || (_cache[29] = _createTextVNode(" Clear filter ", -1))
              ]),
              _: 1
            }),
            _createVNode(_unref(BButton), {
              variant: "outline-dark",
              type: "submit",
              disabled: ComputeTaskListRef.value?.tasksQueryInProgress
            }, {
              default: _withCtx(() => [
                (ComputeTaskListRef.value?.tasksQueryInProgress)
                  ? (_openBlock(), _createElementBlock("span", _hoisted_4, [
                      _createVNode(_unref(BSpinner), {
                        small: "",
                        label: ""
                      }),
                      _cache[30] || (_cache[30] = _createTextVNode(" Searching ", -1))
                    ]))
                  : (_openBlock(), _createElementBlock("span", _hoisted_5, [
                      _createVNode(_unref(FontAwesomeIcon), {
                        icon: "magnifying-glass",
                        class: "me-1"
                      }),
                      _cache[31] || (_cache[31] = _createTextVNode(" Search ", -1))
                    ]))
              ]),
              _: 1
            }, 8, ["disabled"])
          ]),
          _: 1
        })
      ]),
      _createVNode(ComputeTaskList, {
        ref_key: "ComputeTaskListRef",
        ref: ComputeTaskListRef,
        parentName: "Compute Tasks",
        acrossUsers: acrossUsers.value,
        taskId: taskId.value,
        parentId: parentId.value,
        userId: userId.value,
        userRole: userRole.value,
        projectId: projectId.value,
        bucketId: bucketId.value,
        taskType: taskType.value,
        status: status.value,
        namePattern: namePattern.value,
        createdAfter: dateTimeCreatedAfter.value,
        createdBefore: dateTimeCreatedBefore.value,
        updatedAfter: dateTimeUpdatedAfter.value,
        updatedBefore: dateTimeUpdatedBefore.value,
        completedBefore: dateTimeCompletedBefore.value,
        completedAfter: dateTimeCompletedAfter.value,
        abortedAfter: dateTimeAbortedAfter.value,
        abortedBefore: dateTimeAbortedBefore.value,
        pagingItemsPerPage: pagingItemsPerPage.value
      }, null, 8, ["acrossUsers", "taskId", "parentId", "userId", "userRole", "projectId", "bucketId", "taskType", "status", "namePattern", "createdAfter", "createdBefore", "updatedAfter", "updatedBefore", "completedBefore", "completedAfter", "abortedAfter", "abortedBefore", "pagingItemsPerPage"])
    ])
  ], 64))
}
}

})